import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DetailsCopyLink from "../../assets/icons/DetailsCopyLink.svg";
import { generateScript } from "../../services/api"; // We'll create this function in the API service
import { useNavigate } from "react-router-dom";

const ArticleDetailModal = ({
  open,
  handleClose,
  article,
  onScriptGenerated,
}) => {
  const navigate = useNavigate();

  // State for copy notification and script generation
  const [copyNotification, setCopyNotification] = useState({
    open: false,
    message: "",
  });
  const [scriptGenerationState, setScriptGenerationState] = useState({
    loading: false,
    error: null,
    success: false,
  });

  // Handle close notification
  const handleCloseNotification = () => {
    setCopyNotification({
      ...copyNotification,
      open: false,
    });
  };

  // Handle copy link functionality
  const handleCopyLink = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopyNotification({
          open: true,
          message: "Link copied to clipboard!",
        });
      })
      .catch((err) => {
        setCopyNotification({
          open: true,
          message: "Failed to copy link: " + err,
        });
      });
  };

  // Handle script generation
  const handleGenerateScript = async () => {
    if (!article) return;

    setScriptGenerationState({
      loading: true,
      error: null,
      success: false,
    });

    try {
      // Prepare script generation payload
      const payload = {
        article_id: article.id,
        script_name: article.title, // Use article title as script name
      };

      // Call API to generate script
      const response = await generateScript(payload);

      setScriptGenerationState({
        loading: false,
        error: null,
        success: true,
      });

      // Optional: Show success notification
      setCopyNotification({
        open: true,
        message: "Script generated successfully!",
      });

      // Call the callback function to update the article status in the parent component
      if (onScriptGenerated && typeof onScriptGenerated === "function") {
        onScriptGenerated(article.id);
      }

      // Close the modal
      handleClose();

      // Redirect to scripts page after a short delay
      setTimeout(() => {
        navigate("/scripts");
      }, 1000);
    } catch (error) {
      setScriptGenerationState({
        loading: false,
        error: error.message || "Failed to generate script",
        success: false,
      });

      // Show error notification
      setCopyNotification({
        open: true,
        message: `Script generation failed: ${error.message}`,
      });
    }
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Early return if no article, but AFTER all hooks are called
  if (!article) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          width: "700px",
          height: "736px",
          borderRadius: "8px",
          p: 0,
          m: 0,
          overflowY: "visible",
        },
      }}
    >
      {/* Header with title and close button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: "31px",
          pb: "32px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            fontSize: "24px",
            textAlign: "center",
            width: "100%",
          }}
        >
          Detail
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#888",
            position: "absolute",
            right: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{ p: 0, m: 0, "&::-webkit-scrollbar": { display: "none" } }}
      >
        <Box sx={{ p: 4, pt: 3 }}>
          {/* Created, ID, and Headline in one row */}
          <Box
            sx={{
              display: "flex",
              mb: 4,
              border: "1px solid #F1F5F9",
              borderRadius: "8px",
              p: 2,
            }}
          >
            {/* Created Date */}
            <Box sx={{ mr: 4, flex: "1 1 0" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#B7B7B7",
                  fontSize: "12px",
                  fontWeight: "500",
                  mb: 1,
                }}
              >
                CREATED
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CalendarTodayIcon
                  sx={{ fontSize: 18, color: "#6B7280", mr: 1 }}
                />
                <Typography sx={{ color: "#111827", fontSize: "16px" }}>
                  {formatDate(article.created_at)}
                </Typography>
              </Box>
            </Box>

            {/* ID */}
            <Box sx={{ mr: 4, flex: "1 1 0" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#B7B7B7",
                  fontSize: "12px",
                  fontWeight: "500",
                  mb: 1,
                }}
              >
                ID
              </Typography>
              <Typography sx={{ color: "#111827", fontSize: "16px" }}>
                {article.id}
              </Typography>
            </Box>

            {/* Original Headline */}
            <Box sx={{ flex: "2 1 0" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#B7B7B7",
                  fontSize: "12px",
                  fontWeight: "500",
                  mb: 1,
                }}
              >
                Headline
              </Typography>
              <Box sx={{}}>
                <Typography
                  sx={{
                    color: "#161616",
                    fontSize: "12px",
                    lineHeight: "24px",
                  }}
                >
                  {article.title}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Editable Headline Section (with black label and link icon) */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#161616",
                fontWeight: "600",
                mb: 1,
                fontSize: "14px",
              }}
            >
              Headline
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 2,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <Typography sx={{ color: "#4B5563", flex: 1, fontSize: "15px" }}>
                {article.title}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyLink(article.fetched_from)}
              >
                <img src={DetailsCopyLink} alt="copy link" />
              </IconButton>
            </Box>
          </Box>

          {/* Content Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#161616",
                fontWeight: "600",
                mb: 1,
                fontSize: "14px",
              }}
            >
              Content
            </Typography>
            <Box
              sx={{
                color: "#4B5563",
                fontSize: "14px",
                lineHeight: "1.5",
                maxHeight: "180px",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              {article.content.split("\n").map((paragraph, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  paragraph
                  sx={{ fontSize: "14px", mb: 2 }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Script Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#161616",
                fontWeight: "600",
                mb: 1,
                fontSize: "14px",
              }}
            >
              Script
            </Typography>
            <Button
              variant="contained"
              onClick={handleGenerateScript}
              disabled={scriptGenerationState.loading}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                bgcolor: "#378DEA",
                color: "#FFFFFF",
                fontWeight: "500",
                boxShadow: "none",
                px: 3,
                py: 1,
                fontSize: "12px",
                "&:hover": {
                  bgcolor: "#1565c0",
                },
                "&:disabled": {
                  bgcolor: "#B0C4DE",
                  color: "#FFFFFF",
                },
              }}
            >
              {scriptGenerationState.loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Generate Script"
              )}
            </Button>
            {scriptGenerationState.error && (
              <Typography
                variant="body2"
                sx={{
                  color: "error.main",
                  mt: 1,
                  fontSize: "12px",
                }}
              >
                {scriptGenerationState.error}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Notification */}
      <Snackbar
        open={copyNotification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={
            copyNotification.message.includes("Failed") ? "error" : "success"
          }
          sx={{ width: "100%" }}
        >
          {copyNotification.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ArticleDetailModal;
